import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { mediaSearchKey, useRealtimeStore } from "../../lib/store.js";
import type { MessageViewModel } from "../../lib/store.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";
import { formatFullDateTime, type TimeZonePref } from "../../lib/datetime.js";
import { ContextMenuPositioner } from "../../components/context-menu.js";
import { openMediaViewer, type MediaViewerItem } from "./media-viewer.js";

// Раздел «Что в чате» (общие медиа), вынесенный из старой карточки профиля.
// Теперь живёт inline в табе «Инфо» правой панели редизайна. Логика та же:
// searchMessages по scope + постраничная догрузка + просмотрщик.
const MEDIA_PAGE_SIZE = 60;

type MediaTab = "photo" | "video" | "voice" | "document" | "link";
const MEDIA_TABS: Array<{ value: MediaTab; label: string }> = [
  { value: "photo", label: "Фото" },
  { value: "video", label: "Видео" },
  { value: "voice", label: "Голосовые" },
  { value: "document", label: "Файлы" },
  { value: "link", label: "Ссылки" }
];

const viewerItemFor = (message: MessageViewModel, chatId: string): MediaViewerItem | null => {
  const content = message.content;
  if (content.kind !== "photo" && content.kind !== "video") {
    return null;
  }
  const url = content.url ?? (content.kind === "photo" ? content.thumbnailUrl : undefined);
  if (!url) {
    return null;
  }
  return {
    kind: content.kind,
    url,
    ...(content.thumbnailUrl ? { thumbnailUrl: content.thumbnailUrl } : {}),
    ...(content.kind === "video" && content.mimeType ? { mimeType: content.mimeType } : {}),
    ...(content.caption ? { caption: content.caption } : {}),
    senderLabel: message.outgoing ? "Вы" : message.senderName ?? "Собеседник",
    occurredAt: message.occurredAt,
    chatId,
    messageId: message.id
  };
};

export const ChatMediaSection = ({ chatId }: { chatId: string }) => {
  const timeZone: TimeZonePref = useUiPrefs((s) => s.timeZone);
  const searchMessages = useRealtimeStore((s) => s.searchMessages);
  const clearSearch = useRealtimeStore((s) => s.clearSearch);
  const requestScrollToMessage = useRealtimeStore((s) => s.requestScrollToMessage);
  const searchState = useRealtimeStore((s) => s.searchStateByChat[mediaSearchKey(chatId)] ?? null);

  const [mediaTab, setMediaTab] = useState<MediaTab | null>("photo");
  const [mediaMenu, setMediaMenu] = useState<{ x: number; y: number; message: MessageViewModel } | null>(
    null
  );

  useEffect(() => {
    if (!mediaTab) {
      return;
    }
    searchMessages(chatId, "", mediaTab, MEDIA_PAGE_SIZE, {
      requestKey: mediaSearchKey(chatId)
    });
    return () => {
      clearSearch(mediaSearchKey(chatId));
    };
  }, [clearSearch, mediaTab, chatId, searchMessages]);

  const mediaResults = useMemo(() => {
    if (!searchState || searchState.scope !== mediaTab) {
      return [];
    }
    return searchState.results;
  }, [mediaTab, searchState]);

  const mediaHasMore = Boolean(searchState && searchState.scope === mediaTab && searchState.hasMore);
  const mediaCursor = searchState?.scope === mediaTab ? searchState.cursor : undefined;
  const mediaLoading = Boolean(searchState?.isLoading && searchState.scope === mediaTab);
  const loadMoreMedia = useCallback((): void => {
    if (!mediaTab || !mediaCursor || mediaLoading) {
      return;
    }
    searchMessages(chatId, "", mediaTab, MEDIA_PAGE_SIZE, {
      cursor: mediaCursor,
      requestKey: mediaSearchKey(chatId)
    });
  }, [mediaCursor, mediaLoading, mediaTab, chatId, searchMessages]);
  const mediaSentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = mediaSentinelRef.current;
    if (!sentinel || !mediaHasMore || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMoreMedia();
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreMedia, mediaHasMore, mediaResults.length]);

  return (
    <section className="chat-profile__section chat-media">
      <div className="chat-profile__media-tabs" role="tablist" aria-label="Содержимое чата">
        {MEDIA_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={mediaTab === tab.value}
            className={
              mediaTab === tab.value
                ? "chat-profile__media-tab chat-profile__media-tab--active"
                : "chat-profile__media-tab"
            }
            onClick={() => setMediaTab(mediaTab === tab.value ? null : tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {mediaTab ? (
        mediaLoading && mediaResults.length === 0 ? (
          <div className="chat-profile__media-empty">
            <span>Загружаем…</span>
            <button
              type="button"
              className="button button--ghost"
              onClick={() =>
                searchMessages(chatId, "", mediaTab, MEDIA_PAGE_SIZE, {
                  requestKey: mediaSearchKey(chatId),
                  force: true
                })
              }
            >
              Повторить
            </button>
          </div>
        ) : mediaResults.length === 0 ? (
          <div className="chat-profile__media-empty">Здесь пока пусто.</div>
        ) : mediaTab === "photo" ? (
          <div className="chat-profile__photo-grid">
            {mediaResults.map((message) => {
              const previewUrl =
                message.content.kind === "photo"
                  ? message.content.url ?? message.content.thumbnailUrl
                  : undefined;
              return (
                <button
                  key={message.id}
                  type="button"
                  className="chat-profile__photo-tile"
                  onClick={() => {
                    const items = mediaResults
                      .map((entry) => viewerItemFor(entry, chatId))
                      .filter((item): item is MediaViewerItem => item !== null);
                    const index = items.findIndex((item) => item.messageId === message.id);
                    if (index >= 0) {
                      openMediaViewer(items, index);
                    } else {
                      requestScrollToMessage(chatId, message.id);
                    }
                  }}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    setMediaMenu({ x: event.clientX, y: event.clientY, message });
                  }}
                  title={`${
                    message.outgoing ? "Вы" : message.senderName ?? "Собеседник"
                  } · ${formatFullDateTime(message.occurredAt, timeZone)} · ПКМ — перейти к сообщению`}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={
                        (message.content.kind === "photo" ? message.content.caption : undefined) ??
                        "Фото"
                      }
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span className="chat-profile__photo-tile-empty">…</span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <ul className="chat-profile__media-list">
            {mediaResults.map((message) => (
              <li key={message.id} className="chat-profile__media-item">
                <button
                  type="button"
                  className="chat-profile__media-link"
                  onClick={() => {
                    const content = message.content;
                    const viewerItem = viewerItemFor(message, chatId);
                    if (viewerItem) {
                      const items = mediaResults
                        .map((entry) => viewerItemFor(entry, chatId))
                        .filter((item): item is MediaViewerItem => item !== null);
                      const index = items.findIndex((item) => item.messageId === message.id);
                      openMediaViewer(items, Math.max(0, index));
                      return;
                    }
                    if (content.kind === "document" && content.url) {
                      window.open(content.url, "_blank", "noopener");
                      return;
                    }
                    if (mediaTab === "link") {
                      const url = message.text.match(/\bhttps?:\/\/\S+/i)?.[0];
                      if (url) {
                        window.open(url, "_blank", "noopener");
                        return;
                      }
                    }
                    requestScrollToMessage(chatId, message.id);
                  }}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    setMediaMenu({ x: event.clientX, y: event.clientY, message });
                  }}
                  title={
                    message.content.kind === "video"
                      ? "Открыть видео · ПКМ — перейти к сообщению"
                      : message.content.kind === "document"
                        ? "Открыть файл · ПКМ — перейти к сообщению"
                        : "Открыть · ПКМ — перейти к сообщению"
                  }
                >
                  <span className="chat-profile__media-meta">
                    <span
                      className={
                        message.outgoing
                          ? "chat-profile__media-author chat-profile__media-author--outgoing"
                          : "chat-profile__media-author"
                      }
                    >
                      {message.outgoing ? "↑ Вы" : `↓ ${message.senderName ?? "Собеседник"}`}
                    </span>
                    <span>{formatFullDateTime(message.occurredAt, timeZone)}</span>
                  </span>
                  <span className="chat-profile__media-text">
                    {message.text.length > 0
                      ? message.text
                      : message.content.kind === "video"
                        ? "Видео"
                        : message.content.kind === "voice"
                          ? "Голосовое"
                          : message.content.kind === "document"
                            ? message.content.fileName ?? "Файл"
                            : "Сообщение"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : (
        <div className="chat-profile__media-hint">Выберите вкладку, чтобы посмотреть содержимое.</div>
      )}
      {mediaTab && mediaResults.length > 0 ? (
        <div className="chat-profile__media-more" ref={mediaSentinelRef}>
          {mediaHasMore ? (
            <button
              type="button"
              className="button button--ghost"
              onClick={loadMoreMedia}
              disabled={mediaLoading}
            >
              {mediaLoading ? "Загружаем…" : `Загрузить ещё (показано ${mediaResults.length})`}
            </button>
          ) : (
            <span className="chat-profile__media-count">Всего: {mediaResults.length}</span>
          )}
        </div>
      ) : null}
      {mediaMenu
        ? createPortal(
            <div
              className="dialog-context-menu__layer"
              onClick={() => setMediaMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setMediaMenu(null);
              }}
              role="presentation"
            >
              <ContextMenuPositioner anchorX={mediaMenu.x} anchorY={mediaMenu.y}>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    requestScrollToMessage(chatId, mediaMenu.message.id);
                    setMediaMenu(null);
                  }}
                >
                  <i className="ic ic-message" aria-hidden="true" />
                  Перейти к сообщению
                </button>
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}
    </section>
  );
};
